'use client';

import { Link } from 'react-router-dom';
import { Home, Check, ArrowRight, ShieldCheck, Navigation, Clock, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export function HomeCare() {
  return (
    <section id="domiciliar" className="py-16 md:py-24 bg-gradient-to-br from-[#0A2540] via-[#0D3866] to-[#0A2540] dark:from-card dark:via-background dark:to-card text-white dark:text-foreground border-y border-transparent dark:border-border/80 relative overflow-hidden transition-colors duration-300">
      {/* Background ambient lighting */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-400/10 dark:bg-primary/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="container-landing relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 dark:bg-primary/15 border border-white/20 dark:border-primary/30 text-blue-200 dark:text-primary text-xs font-semibold uppercase tracking-wider mb-6">
              <Home className="w-3.5 h-3.5 text-blue-300 dark:text-primary" />
              <span>Cuidado que vai até você</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white dark:text-foreground tracking-tight leading-[1.15] mb-6">
              Consulta domiciliar, <br className="hidden sm:inline" />
              sem sair de casa.
            </h2>

            <p className="text-base sm:text-lg text-blue-100/80 dark:text-muted-foreground leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              Ideal para idosos, crianças, pacientes em recuperação ou para quem valoriza a tranquilidade
              e privacidade do próprio lar com atendimento presencial de alto nível.
            </p>

            <ul className="space-y-3 mb-8 text-sm sm:text-base text-blue-50/90 dark:text-foreground/90 text-left max-w-md mx-auto lg:mx-0">
              <li className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 dark:text-emerald-400 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 font-bold" />
                </span>
                <span>Médicos verificados e com equipamentos para atendimento no local</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 dark:text-emerald-400 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 font-bold" />
                </span>
                <span>Acompanhamento da chegada do profissional em tempo real</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 dark:text-emerald-400 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 font-bold" />
                </span>
                <span>Pagamento 100% protegido direto pela plataforma</span>
              </li>
            </ul>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <Link to="/register?type=home" className="w-full sm:w-auto">
                <Button variant="primary" size="lg" className="w-full sm:w-auto rounded-xl font-bold bg-white text-blue-900 hover:bg-blue-50 dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary-hover gap-2 shadow-lg dark:shadow-primary/25">
                  <span>Solicitar consulta domiciliar</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Visual: Home Care Arrival Experience Card */}
          <div className="flex items-center justify-center">
            <div className="w-full max-w-sm bg-white/10 dark:bg-card/90 backdrop-blur-md border border-white/20 dark:border-white/[0.08] rounded-3xl p-6 shadow-2xl dark:shadow-[0_20px_50px_rgba(0,0,0,0.6),inset_0_1px_0_0_rgba(255,255,255,0.08)] space-y-4">
              {/* Header Badge */}
              <div className="flex items-center justify-between pb-3 border-b border-white/10 dark:border-border/60 text-xs">
                <span className="font-semibold text-blue-200 dark:text-foreground flex items-center gap-1.5">
                  <Navigation className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                  Atendimento Domiciliar
                </span>
                <span className="px-2 py-0.5 rounded-full bg-emerald-400/20 text-emerald-300 font-bold text-[10px]">
                  ATIVO
                </span>
              </div>

              {/* Central Map Pin Graphic */}
              <div className="py-6 flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 rounded-3xl bg-white/10 dark:bg-muted/40 border border-white/20 dark:border-white/[0.08] flex items-center justify-center mb-3 shadow-inner">
                  <Home className="w-10 h-10 text-blue-200 dark:text-primary" />
                </div>
                <h4 className="font-bold text-base text-white dark:text-foreground">Seu endereço residencial</h4>
                <p className="text-xs text-blue-200/70 dark:text-muted-foreground mt-0.5">Médico a caminho com material de exame clínico</p>
              </div>

              {/* Status Box */}
              <div className="p-3.5 rounded-2xl bg-black/20 dark:bg-muted/30 border border-white/10 dark:border-border/60 space-y-2 text-xs">
                <div className="flex items-center justify-between text-blue-100 dark:text-foreground">
                  <span className="flex items-center gap-1.5">
                    <UserCheck className="w-3.5 h-3.5 text-emerald-400" />
                    Profissional confirmado
                  </span>
                  <span className="font-bold text-white dark:text-foreground">Dr. Plantonista</span>
                </div>
                <div className="flex items-center justify-between text-blue-200/80 dark:text-muted-foreground pt-1 border-t border-white/10 dark:border-border/40">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-blue-300 dark:text-primary" />
                    Previsão de chegada
                  </span>
                  <span className="font-bold text-emerald-300 dark:text-emerald-400">Horário agendado</span>
                </div>
              </div>

              {/* Trust Footer */}
              <div className="flex items-center justify-center gap-2 text-[11px] text-blue-200/70 dark:text-muted-foreground pt-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                <span>Protocolo de biossegurança certificado</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
